import React from 'react';

import TextQuestion from './TextQuestion';
import InfoMessage from './InfoMessage';
import RadioQuestion from './RadioQuestion';
import CheckboxQuestion from './CheckboxQuestion';
import DateQuestion from './DateQuestion';
import { filterAutoUnselectedFields, parseNameValue } from '../safetyQuestionFunctions';

interface QuestionRowProps {
  question: any;
  checkinContext: any;
  piiContext: any;
  updateCheckinContext: (reduxObj: any) => void;
  checkIfShownWhenTrue: (question: any) => boolean;
  readOnly?: boolean;
}

const QuestionRow: React.FC<QuestionRowProps> = ({
  question,
  checkinContext,
  piiContext,
  updateCheckinContext,
  checkIfShownWhenTrue,
  readOnly = false,
}: QuestionRowProps) => {
  const hideNested = (value, reduxObj, parentField) => {
    // Clear nested fields that match the deselected value
    // Only clear fields that are under the same parent and have the value as a direct segment
    // e.g., if "Neurostimulator" is deselected from "MedicalMetal.Spine.Type",
    // clear "MedicalMetal.Spine.Neurostimulator" and "MedicalMetal.Spine.Neurostimulator.Prohibited"
    // Use dot-boundary check so we don't clear "MedicalMetal.Spine.NeurostimulatorMRI" when clearing "Neurostimulator"
    const parentPath = parentField ? parentField.split('.').slice(0, -1).join('.') : '';

    if (!parentPath) {
      return;
    }

    // Match exactly parentPath.value or parentPath.value.* (prefix must be followed by dot or end)
    const expectedPrefix = `${parentPath}.${value}`;

    Object.keys(reduxObj).forEach((key) => {
      const isExactMatch = key === expectedPrefix;
      const isNestedUnderPrefix = key.startsWith(`${expectedPrefix}.`);
      if ((isExactMatch || isNestedUnderPrefix) && key !== parentField) {
        reduxObj[key] = '';
      }
    });
  };

  const handleCheck = (checkValue) => {
    let reduxObj;
    const { name, value } = parseNameValue(checkValue);

    if (name.includes('Type') && name !== 'ReasonType' && !Array.isArray(checkinContext[name])) {
      reduxObj = { ...checkinContext, [name]: [value] };
    } else if (Array.isArray(checkinContext[name])) {
      const state = [...checkinContext[name]]; // Create a copy to avoid mutating the original
      const index = state.indexOf(value);

      if (index === -1) {
        state.push(value);
        reduxObj = { ...checkinContext, [name]: state };
      } else {
        state.splice(index, 1);
        reduxObj = { ...checkinContext, [name]: state };
        // Clear nested fields after creating reduxObj
        // Only clear if the value is actually removed (not in the new state)
        if (!state.includes(value)) {
          hideNested(value, reduxObj, name);
        }
      }
    } else if (checkinContext[name] !== value) {
      reduxObj = filterAutoUnselectedFields(checkinContext, name, value)
    }
    if (reduxObj) {
      updateCheckinContext(reduxObj);
    }
  };

  if (!checkIfShownWhenTrue(question)) {
    return null;
  }

  const parent = question.field;
  const value = checkinContext[parent];

  const renderMainQuestion = () => {
    switch (question.type) {
      case 'datepicker':
        const handleDateInput = (name, timeChange) => {
          if (timeChange) {
            updateCheckinContext({
              [name]: timeChange.hours(0).minutes(0).seconds(0).format('YYYY-MM-DDTHH:mm:ss.SSSSZ'),
            });
          }
        };
        return (
          <DateQuestion
            title={question.title}
            value={value}
            parent={parent}
            handleCheck={handleDateInput}
            readOnly={readOnly}
          />
        );
      case 'text':
        const handleTextInput = (name, value) => {
          updateCheckinContext({ [name]: value });
        };
        return (
          <TextQuestion
            handleCheck={handleTextInput}
            title={question.title}
            value={value}
            parent={parent}
            show={question.show}
            width={question.width}
            readOnly={readOnly}
          />
        );
      case 'info':
        return (
          <InfoMessage
            text={question.text}
            bold={question.title}
            backgroundColor={question?.config?.color}
          />
        );
      case 'radio':
        return (
          <RadioQuestion
            title={question.title}
            options={question.options}
            handleCheck={handleCheck}
            parent={parent}
            show={question.show}
            value={value}
            readOnly={readOnly}
          />
        );
      case 'choice':
        return (
          <CheckboxQuestion
            title={question.title}
            options={question.options}
            handleCheck={handleCheck}
            parent={parent}
            show={question.show}
            value={value}
            readOnly={readOnly}
          />
        );
      default:
        return null;
    }
  };
  const renderSubquestions = () =>
    question.subs && question.subs.map((subQuestion, index) => (
      <QuestionRow
        question={subQuestion}
        checkinContext={checkinContext}
        piiContext={piiContext}
        updateCheckinContext={updateCheckinContext}
        checkIfShownWhenTrue={checkIfShownWhenTrue}
        readOnly={readOnly}
        key={`${subQuestion.field}-${index}-row`}
      />
    ));
  return (
    <>
      {renderMainQuestion()}
      {renderSubquestions()}
    </>
  );
};

export default QuestionRow;
